




































































































import { Component, Prop, Watch } from 'vue-property-decorator';
import CommonVue from '../models/commonVue';
import Project from '../api/project';
import LockManager from '../models/lock';
import Media from '../api/media';

import { MediaFileField } from '../models/fields';
import { BusEvent } from '../models/bus';
import FieldInternational from '../components/FieldInternational.vue';
import FieldImage from '../components/FieldImage.vue';
import EditIcon from '../components/icons/EditIcon.vue';

@Component({
  components: {
    'field-international': FieldInternational,
    'field-image': FieldImage,
    'edit-icon': EditIcon
  }
})
export default class EditProject extends CommonVue {
  @Prop({ type: Number }) public projectId!: number;
  @Prop() public id?: number;
  @Prop({ type: String, default: Project.SLUG }) public parent: string;

  public project: Project = null;
  public projectName: string = '';

  public async beforeMount() {
    await this.load();
  }

  @Watch('id')
  public async load() {
    this.busy = true;
    this.error = false;

    this.ignoreFirstEntityChange = true;
    await this.loadProject();
    this.ignoreFirstEntityChange = false;
    this.updateName();

    if (this.project.id) {
      this.lockManager = new LockManager(this.PROJECT_SLUG, this.project.id);
      await this.lockManager.checkStatus();
      if (this.lockManager.isBusy) {
        this.$bus.$emit(
          BusEvent.ERROR,
          'Someone else is making changes.',
          new Error(
            'Click refresh to check if they have finished their changes'
          )
        );
      }
    } else {
      this.lockManager = null;
    }

    this.busy = false;
  }

  public async loadProject(): Promise<void> {
    if (!this.id) {
      this.project = new Project();
    } else {
      try {
        this.project = await Project.get(this.id);
      } catch (e) {
        this.error = true;
        this.$bus.$emit(BusEvent.ERROR, 'Could not get project information', e);
      }
    }
  }

  public updateName() {
    let name = this.project.name.getDefaultValue();
    if (!name) {
      name = '';
    }
    this.projectName = name;
  }

  public async deleteClicked(): Promise<void> {
    if (!this.project || !this.project.id) {
      return;
    }
    this.busy = true;
    try {
      if (!this.lockManager) {
        this.lockManager = new LockManager(this.PROJECT_SLUG, this.project.id);
      }

      await this.tryToLock();
      await this.project.delete();
      await this.lockManager.releaseLock();

      this.busy = false;
      this.$router.push(`/${this.PROJECT_SLUG}/`);
    } catch (e) {
      this.busy = false;
      this.$bus.$emit(BusEvent.ERROR, 'Could not delete project', e);
    }
  }

  public async saveClicked(): Promise<void> {
    if (!this.project) {
      return;
    }
    this.busy = true;
    try {
      this.checkMandatoryFields();

      await this.saveProject();

      this.busy = false;
      this.$router.push(`/${this.PROJECT_SLUG}/`);
    } catch (e) {
      this.busy = false;
      this.$bus.$emit(BusEvent.ERROR, 'Could not save project', e);
    }
  }

  public checkMandatoryFields() {
    // Check if name is filled
    const defaultName = this.project.name.getDefaultValue();
    if (defaultName === undefined || defaultName === '') {
      throw Error('Please insert an English name for your project.');
    }
  }

  public async saveProject(): Promise<void> {
    const isNew = !this.project.id;

    if (!isNew) {
      if (!this.lockManager && this.project && this.project.id) {
        this.lockManager = new LockManager(this.PROJECT_SLUG, this.project.id);
      }
      await this.tryToLock();
      await this.saveMediaFiles();
    }

    // Upload project
    await this.project.save();

    if (this.lockManager && this.lockManager.isLocked) {
      await this.lockManager.releaseLock();
    }
  }

  public async saveMediaFiles(): Promise<void> {
    if (!this.project || !this.project.id) {
      throw Error('Project has not been created yet.');
    }

    this.imagesToDelete.forEach(async (id) => await Media.deleteImage(id));
    this.imagesToDelete = [];

    if (!this.isNew) {
      const icon = await this.saveMediaFile(this.$refs.icon, 'icon');
      if (icon !== null) {
        this.project.icon = icon;
      }
    }
  }

  public async saveMediaFile(
    field: any,
    fieldName: string
  ): Promise<number | null | undefined> {
    if (!this.project || !this.project.id) {
      return null;
    }
    const mediaFileField = (field as any) as MediaFileField;
    if (mediaFileField.hasNewMedia()) {
      const result = await mediaFileField.upload(
        this.PROJECT_SLUG,
        this.project.id,
        fieldName
      );
      return result;
    } else if (!mediaFileField.hasMedia()) {
      return undefined;
    } else {
      return null;
    }
  }
}
